import { useId } from 'react';
import { Box } from '@mui/material';

// ---------------------------------------------------------------------------
// Sigla, desenată în cod.
//
// Înainte venea din `QuantikFlow-nightMode.png` — 1,1 MB, adică mai mult decât
// tot restul paginii la un loc, pe o conexiune de telefon. În plus era o
// imagine cu fundal negru, deci nu putea sta decât pe negru.
//
// Aici e același semn (grila de proiect + săgeata care urcă prin ea), ca
// vector: câțiva octeți, clar la orice mărime, și își ia culoarea din pagină —
// pe fundal deschis sau închis, aceeași siglă.
//
// Tăietura din jurul săgeții e o mască, nu un contur pictat în culoarea
// fundalului: așa rămâne transparentă și peste sticlă, și peste bleumarin.
// ---------------------------------------------------------------------------

// Grila: 3 coloane × 3 rânduri, pătrat de 14 și interval de 3 (3×14 + 2×3 =
// 48, exact caseta). Prima variantă avea 4×4 — 16 pătrate care, la 26px în
// bara de meniu, se amestecau într-o textură din care nu se mai citea nimic.
const CELLS = [0, 17, 34];
const S = 14;

// Săgeata: două diagonale la 45° legate printr-un palier — profilul unui
// grafic de progres care avansează în trepte și iese prin colțul din dreapta
// sus, peste grilă.
const ARROW = 'M 5 42 L 16 31 L 23 31 L 41 13';
const HEAD = '47 7 47 20 34 7';

export function LogoMark({ size = 32, sx }) {
  // Fiecare instanță are nevoie de un id propriu; două măști cu același id în
  // aceeași pagină, și a doua siglă o folosește pe prima.
  const maskId = `qf-logo-cut-${useId().replace(/:/g, '')}`;

  return (
    <Box
      component="svg"
      viewBox="0 0 48 48"
      role="img"
      aria-label="QuantikFlow"
      sx={{ width: size, height: size, display: 'block', flex: 'none', color: 'var(--qf-accent)', ...sx }}
    >
      <defs>
        <mask id={maskId}>
          {/* Alb = se vede, negru = se scoate. Banda neagră e chiar traseul
              săgeții, îngroșat, deci grila se retrage din calea ei. */}
          <rect x="0" y="0" width="48" height="48" fill="#fff" />
          <path d={ARROW} stroke="#000" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <polygon points={HEAD} fill="#000" stroke="#000" strokeWidth="5.5" strokeLinejoin="round" />
        </mask>
      </defs>

      <g mask={`url(#${maskId})`} fill="currentColor">
        {CELLS.map((y) =>
          CELLS.map((x) => <rect key={`${x}-${y}`} x={x} y={y} width={S} height={S} rx="2.6" />)
        )}
      </g>

      <path
        d={ARROW}
        fill="none"
        stroke="currentColor"
        strokeWidth="5.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon points={HEAD} fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </Box>
  );
}

// Sigla completă: semnul plus numele, aliniate optic pe aceeași linie de bază.
export default function Logo({ size = 30, showName = true, nameSx, sx }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.15, minWidth: 0, ...sx }}>
      <LogoMark size={size} />
      {showName && (
        <Box
          component="span"
          sx={{
            fontSize: size * 0.6,
            fontWeight: 600,
            letterSpacing: '-0.022em',
            whiteSpace: 'nowrap',
            color: 'inherit',
            ...nameSx,
          }}
        >
          QuantikFlow
        </Box>
      )}
    </Box>
  );
}
